/**
 * Test Session Login Component
 *
 * The way in staff hand out for a playtest (?mode=test-session): the player
 * types their team's code and a name, and the server resolves the code to the
 * team's session, world and class (resolve_team_code) and joins its match —
 * the component only collects the two and delegates to
 * authService.connectFromTestSession.
 *
 * A form in the way in's card, with no validation of the browser's, whose
 * bubble speaks the browser's language and cannot be styled: what is missing
 * is said by the game at the field it concerns — a team code has 4 to 8
 * digits; the name the team will see — and focus goes to the first. Join is
 * always the gold, always pressable. The server's answer stands in a notice
 * above Join — a code no team has at the code field, where it is the player's
 * to fix — said by its code, a start in the player's language. While the join
 * runs the fields rest and its steps take Join's place. A join remembers the
 * code and the name in this browser for next time, and with both remembered,
 * Join takes the focus, so Enter joins.
 */

import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { refusalWords, type JoinRefusal } from '../../domains/join-refusals';
import { useAuthStore } from '../../domains/auth';
import { authService } from '../../services/authentication/auth-service';
import { kickOffDocumentFullscreenFromGesture, shouldEnforceMatchFullscreen } from '../../utils/fullscreen';
import { Button } from '../ui/button';
import { TextField } from '../ui/text-field';
import { EntryCard, EntryStatus } from './entry-card';
import { EntryFrame } from './entry-frame';
import type { StatusPart } from './join-lines';
import styles from './test-session-login.module.css';
import { rememberJoin, rememberedJoin } from './test-session-memory';

/** The longest name a player may give. */
const MAX_NAME_LENGTH = 12;
/** A team code: four to eight digits. */
const TEAM_CODE = /^\d{4,8}$/;
const CODE_FIELD = 'ts-team-code';
const NAME_FIELD = 'ts-user-name';

/** What is wrong with each field, said at the field. */
interface FieldErrors {
  code?: string;
  name?: string;
}

export function TestSessionLogin({ search }: { search: string }) {
  const { t } = useTranslation();
  const step = useAuthStore((s) => s.connectionStep);
  const joining = useAuthStore((s) => s.connectionStatus === 'connecting');

  const [code, setCode] = useState(() => rememberedJoin().code);
  const [name, setName] = useState(() => rememberedJoin().name);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  // The server's answer to the last join, until the player changes a field.
  const [answer, setAnswer] = useState<JoinRefusal | null>(null);
  // Both remembered: Join has the focus, and Enter joins.
  const [bothRemembered] = useState(() => code !== '' && name.trim() !== '');

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (joining) return;
    const typed = code.trim();
    const player = name.trim();

    const errors: FieldErrors = {
      code: TEAM_CODE.test(typed) ? undefined : t('testSession.error.invalidCode'),
      name: player ? undefined : t('testSession.error.nameRequired'),
    };
    setFieldErrors(errors);
    setAnswer(null);
    if (errors.code || errors.name) {
      document.getElementById(errors.code ? CODE_FIELD : NAME_FIELD)?.focus();
      return;
    }

    if (shouldEnforceMatchFullscreen()) kickOffDocumentFullscreenFromGesture();
    await authService.connectFromTestSession(typed, player);
    const refusal = useAuthStore.getState().refusal;
    if (!refusal) {
      rememberJoin(typed, player);
      return;
    }
    setAnswer(refusal);
    // A code no team has is the player's to fix, at its field.
    if (refusal.code === 'team_code_not_found') document.getElementById(CODE_FIELD)?.focus();
  };

  // The server's answer in its words; a code no team has is said at the code's field.
  const words = answer && refusalWords(answer);
  const codeNotFound = answer?.code === 'team_code_not_found';
  // Any other refusal is the card's, under the fields; and the join's steps, once it is under way.
  const refused = words && !codeNotFound ? words : null;
  const parts: StatusPart[] = joining ? [{ kind: 'step', step: step ?? 'authenticating' }] : refused ? [{ kind: 'refusal', words: refused }] : [];

  return (
    <EntryFrame search={search} title={t('testSession.title')} meaning={t('testSession.subtitle')}>
      <EntryCard>
        <form className={styles.form} noValidate onSubmit={(event) => void submit(event)}>
          <TextField
            id={CODE_FIELD}
            code
            label={t('testSession.teamCodeLabel')}
            placeholder={t('testSession.teamCodePlaceholder')}
            inputMode="numeric"
            autoComplete="off"
            maxLength={8}
            value={code}
            disabled={joining}
            autoFocus={!bothRemembered && code === ''}
            error={fieldErrors.code ?? (codeNotFound ? words?.title : undefined)}
            onChange={(event) => {
              setCode(event.target.value.replace(/\D/g, ''));
              setFieldErrors((errors) => ({ ...errors, code: undefined }));
              setAnswer(null);
            }}
          />
          <TextField
            id={NAME_FIELD}
            label={t('testSession.userNameLabel')}
            hint={t('testSession.userNameHint', { figure: MAX_NAME_LENGTH })}
            count={`${name.length} / ${MAX_NAME_LENGTH}`}
            maxLength={MAX_NAME_LENGTH}
            value={name}
            disabled={joining}
            autoFocus={!bothRemembered && code !== ''}
            error={fieldErrors.name}
            onChange={(event) => {
              setName(event.target.value.slice(0, MAX_NAME_LENGTH));
              setFieldErrors((errors) => ({ ...errors, name: undefined }));
              setAnswer(null);
            }}
          />
          <EntryStatus parts={parts} />
          {!joining && (
            <Button type="submit" tone="primary" block autoFocus={bothRemembered}>
              {t('testSession.joinButton')}
            </Button>
          )}
        </form>
      </EntryCard>
    </EntryFrame>
  );
}
